import React, {
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import ElementOutline, { OutlineType } from "../../SimpleCanvas/ElementOutline";
import DropIndicator from "../../SimpleCanvas/DropIndicator";
import { TempoElement } from "@/utils/tempoElement";
import { findElementByDomUniquePath } from "../utils/utils";
import ResizableOutline, {
  ItemRect,
} from "../../SimpleCanvas/ResizableOutline";
import _ from "lodash";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { SimpleCanvasRef } from "@/components/SimpleCanvas/SimpleCanvas";
import { useSimpleCanvasStore } from "@/components/SimpleCanvas/simpleCanvasStore";
import { getUpdateClassChangeForElement } from "@/components/RightStyle/TailwindStylingPanel/utils/updateElementUtils";
import { cssEval } from "./StaticIframe/hooks/useExtractCss";

interface OutlineSystemProps {
  storyboardId: string;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  resizeDomElement?: HTMLElement;
  storyboardWidth: number;
  storyboardHeight: number;
  storyboardX: number;
  storyboardY: number;
  htmlForStoryboard: string;
  hideAllOutlines: boolean;
  simpleCanvasRef: React.RefObject<SimpleCanvasRef>;
  setIsResizing: (isResizing: boolean) => void;
}

export interface OutlineSystemRef {
  refreshOutlines: () => void;
}

const OutlineSystem = forwardRef<OutlineSystemRef, OutlineSystemProps>(
  (
    {
      storyboardId,
      iframeRef,
      resizeDomElement,
      storyboardWidth,
      storyboardHeight,
      storyboardX,
      storyboardY,
      htmlForStoryboard,
      hideAllOutlines,
      simpleCanvasRef,
      setIsResizing,
    },
    ref,
  ) => {
    // State to force re-render when refresh is called
    const [refreshKey, setRefreshKey] = useState(0);
    const resizingDimensionsRef = useRef<{
      width: number;
      height: number;
    } | null>(null);

    const { hoveredElementKey, storyboardIdHovered } = useHoveredNavTreeNode();
    const { selectedElement, selectedElementKey, storyboardIdSelected } =
      useSelectedNavTreeNode();

    const { multiselectedElementKeys, multiselectedStoryboardIds } =
      useMultiselectedNavTreeNode();

    const sbIdToCodebaseIdToNavNodeLookup = useCanvasStore(
      (state) => state.sbIdToCodebaseIdToNavNodeLookup,
    );

    // Expose refresh method to parent components
    useImperativeHandle(
      ref,
      () => ({
        refreshOutlines: () => {
          setRefreshKey((prev) => prev + 1);
        },
      }),
      [],
    );

    const getElementInfo = useCallback(
      (elementKey: string) => {
        const tempoElement = TempoElement.fromKey(elementKey);

        // The simple canvas will outline the storyboard for us
        if (tempoElement.isStoryboard(storyboardId)) {
          return null;
        }

        const navNode =
          useCanvasStore.getState().elementKeyToNavNodeLookup[elementKey];
        if (!navNode) {
          return null;
        }

        const iframeDoc =
          iframeRef.current?.contentDocument ||
          iframeRef.current?.contentWindow?.document ||
          null;

        const element = findElementByDomUniquePath(
          iframeDoc ?? undefined,
          navNode.domUniquePath ?? navNode.domUniquePathForComponent,
        );
        if (!element) return null;

        const rect = element.getBoundingClientRect();

        return {
          tagName: navNode.elementTagName || navNode.name,
          isComponent: navNode.isComponent || false,
          codebaseId: navNode.tempoElement?.codebaseId || "unknown",
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          display: cssEval(iframeDoc, element, "display"),
          element,
          position: cssEval(iframeDoc, element, "position"),
        };
      },
      [iframeRef, storyboardId],
    );

    const generateElementOutlines = (): JSX.Element[] => {
      if (hideAllOutlines || !htmlForStoryboard || refreshKey === null) {
        return [];
      }

      const outlines: JSX.Element[] = [];

      const hoveredElement = TempoElement.fromKey(hoveredElementKey);

      // Hovered element outline
      if (
        hoveredElement?.isKnownElement() &&
        hoveredElement.storyboardId === storyboardId &&
        hoveredElementKey !== selectedElementKey &&
        // Simple canvas will outline the storyboard for us
        !storyboardIdHovered
      ) {
        const info = getElementInfo(hoveredElementKey);

        if (info && info.display !== "none") {
          outlines.push(
            <ElementOutline
              key={`hovered-${hoveredElementKey}`}
              type={OutlineType.CHILD}
              itemRect={{
                x: info.left,
                y: info.top,
                width: info.width,
                height: info.height,
              }}
              tagName={info.tagName}
              isComponent={info.isComponent}
              iframeRef={iframeRef}
            />,
          );
        }
      }

      // Multi-selected elements outlines
      if (
        multiselectedElementKeys &&
        multiselectedElementKeys.length &&
        // Simple canvas will outline the storyboard for us
        !multiselectedStoryboardIds?.length
      ) {
        multiselectedElementKeys.forEach((elementKey, index) => {
          const element = TempoElement.fromKey(elementKey);

          if (!element.isKnownElement()) {
            return;
          }

          if (element.storyboardId !== storyboardId) {
            return;
          }

          const info = getElementInfo(elementKey);

          if (info && info.display !== "none") {
            const isMainSelection = elementKey === selectedElementKey;
            outlines.push(
              <ElementOutline
                key={`multi-${elementKey}-${index}`}
                type={
                  isMainSelection ? OutlineType.PRIMARY : OutlineType.SECONDARY
                }
                itemRect={{
                  x: info.left,
                  y: info.top,
                  width: info.width,
                  height: info.height,
                }}
                selected={isMainSelection}
                tagName={info.tagName}
                isComponent={info.isComponent}
                iframeRef={iframeRef}
              />,
            );
          }
        });
      } else if (
        selectedElement.isKnownElement() &&
        selectedElement.storyboardId === storyboardId &&
        selectedElementKey &&
        // Simple canvas will outline the storyboard for us
        !storyboardIdSelected
      ) {
        // Single selected element outline
        const info = getElementInfo(selectedElementKey);

        if (info && info.display !== "none") {
          // TODO: For absolutely positioned elements, allow moving them
          const itemRect: ItemRect = {
            x: info.left,
            y: info.top,
            width: info.width,
            height: info.height,
            tagName: info.tagName,
            isComponent: info.isComponent,
          };

          const codebaseId = selectedElement.codebaseId;

          outlines.push(
            <ResizableOutline
              ref={(el) => {
                if (el) {
                  useSimpleCanvasStore
                    .getState()
                    .setItemToResizableOutlineRef(codebaseId, el);
                } else {
                  useSimpleCanvasStore
                    .getState()
                    .setItemToResizableOutlineRef(codebaseId, null);
                }
              }}
              key={`selected-${selectedElementKey}`}
              itemRect={itemRect}
              elementsResizing={[info.element as HTMLElement]}
              onItemChange={() => {}}
              onResizeStart={() => {
                resizingDimensionsRef.current = {
                  width: info.width,
                  height: info.height,
                };
                setIsResizing(true);
              }}
              onResizeEnd={({ width, height }) => {
                const classUpdate = getUpdateClassChangeForElement({
                  codebaseId: selectedElement.codebaseId,
                  newWidth: width,
                  newHeight: height,
                  oldWidth: resizingDimensionsRef.current?.width,
                  oldHeight: resizingDimensionsRef.current?.height,
                });
                if (classUpdate) {
                  useDesignCommandManager
                    .getState()
                    .executeCommand(classUpdate);
                }

                setIsResizing(false);
                resizingDimensionsRef.current = null;
              }}
              canMove={false}
              iframeRef={iframeRef}
              clientToCanvas={(x, y) =>
                simpleCanvasRef?.current?.clientToCanvas(x, y)
              }
            />,
          );
        }
      }

      // Secondary outlines for elements with same codebase ID
      if (
        selectedElement.isKnownElement() &&
        selectedElementKey &&
        selectedElement.storyboardId === storyboardId
      ) {
        const selectedInfo = getElementInfo(selectedElementKey);
        if (selectedInfo?.codebaseId) {
          const codebaseIdToNavNodeLookup =
            sbIdToCodebaseIdToNavNodeLookup[storyboardId];

          const relatedElements =
            codebaseIdToNavNodeLookup?.[selectedInfo.codebaseId] || [];

          relatedElements.forEach((navNode, index) => {
            const elementKey = navNode.tempoElement?.getKey();

            if (
              elementKey &&
              elementKey !== selectedElementKey &&
              !(
                multiselectedElementKeys &&
                multiselectedElementKeys.includes(elementKey)
              ) &&
              elementKey !== hoveredElementKey
            ) {
              const info = getElementInfo(elementKey);

              if (info && info.display !== "none") {
                outlines.push(
                  <ElementOutline
                    key={`secondary-${elementKey}-${index}`}
                    type={OutlineType.SECONDARY}
                    itemRect={{
                      x: info.left,
                      y: info.top,
                      width: info.width,
                      height: info.height,
                    }}
                    tagName={navNode.elementTagName || navNode.name}
                    isComponent={navNode.isComponent || false}
                    iframeRef={iframeRef}
                  />,
                );
              }
            }
          });
        }
      }

      if (
        hoveredElement?.isStoryboard(storyboardId) &&
        hoveredElementKey !== selectedElementKey
      ) {
        outlines.push(
          <ElementOutline
            key={`storyboard-${hoveredElementKey}`}
            type={OutlineType.PRIMARY}
            itemRect={{
              x: 0,
              y: 0,
              width: storyboardWidth,
              height: storyboardHeight,
            }}
            tagName="Storyboard"
            isComponent={true}
            iframeRef={iframeRef}
          />,
        );
      }

      // Add drop indicator (it will handle its own visibility logic)
      outlines.push(
        <DropIndicator
          key="drop-indicator"
          storyboardId={storyboardId}
          iframeRef={iframeRef}
        />,
      );

      return outlines;
    };

    const outlines = useMemo(generateElementOutlines, [
      hoveredElementKey,
      selectedElementKey,
      multiselectedElementKeys,
      sbIdToCodebaseIdToNavNodeLookup,
      hideAllOutlines,
      refreshKey,
      storyboardId,
      storyboardWidth,
      storyboardHeight,
      getElementInfo,
      iframeRef,
      selectedElement,
      htmlForStoryboard,
      storyboardIdHovered,
      multiselectedStoryboardIds,
      storyboardIdSelected,
      simpleCanvasRef,
      setIsResizing,
    ]);

    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        {outlines}
      </div>
    );
  },
);

export default OutlineSystem;
